import _ from 'lodash';
import React, {useEffect} from 'react';
import styles from './Brad.module.scss';
import posed from 'react-pose';
import makeNimbus from './Nimbus';
import {keysToSingleValue} from '../utils/objectFactory';
import {
    allPoses,
    questionPoses,
    wrongPoses,
    rightPoses,
    waitPoses,
    listenPoses,
    lyricsPoses,
} from '../gameHelpers';
import * as audioBundle from '../audioBundle';

const brad_duration = 1000;

const waitTry_brad_duration = brad_duration;
const waitTry_team_duration = 3000;
const waitTry_hint_delay = waitTry_brad_duration + waitTry_team_duration;
const waitTry_hint_duration = 500;
const waitTry_nimbus_delay = waitTry_hint_delay + waitTry_hint_duration;
const waitTry_nimbus_duration = 1000;

const discussionWait_hint_arrow_delay = 1000;
const discussionWait_hint_arrow_duration = 500;
const discussionWait_hint_delay =
    discussionWait_hint_arrow_delay + discussionWait_hint_arrow_duration;
const discussionWait_hint_duration = 750;
const discussionWait_hint_nimbus_delay =
    discussionWait_hint_delay + discussionWait_hint_duration;
const discussionWait_hint_nimbus_duration = 1000;

const Container = posed.div({
    ...keysToSingleValue(lyricsPoses, {
        scaleX: -1,
        transition: {duration: 0},
    }),
    ...keysToSingleValue(_.difference(allPoses, lyricsPoses), {
        scaleX: 1,
        transition: {duration: 0},
    }),
});

const BradWaitIcon = posed.div({
    initial: {
        scale: 0,
        rotate: 0,
        transition: {duration: 0},
    },
    ...keysToSingleValue(waitPoses, {
        scale: 1,
        rotate: 0,
        transition: {
            duration: brad_duration,
        },
    }),
    ...keysToSingleValue(_.without(allPoses, 'initial', ...waitPoses), {
        scale: 0,
        rotate: -90,
        applyAtEnd: {rotate: 90},
        transition: {
            duration: brad_duration,
        },
    }),
});

const rotatedIconAnimation = (...poses) => ({
    void: {
        rotate: 90,
    },
    ...keysToSingleValue(poses, {
        scale: 1,
        rotate: 0,
        transition: {
            duration: brad_duration,
        },
    }),
    ...keysToSingleValue(_.without(allPoses, ...poses), {
        scale: 0,
        rotate: -90,
        applyAtEnd: {rotate: 90},
        transition: {
            duration: brad_duration,
        },
    }),
});

const bradTransitionPoses = _.without(
    _.union(waitPoses, listenPoses, questionPoses, wrongPoses, rightPoses),
    'wrong-answer-multiple'
);

const BradListenIcon = posed.div(rotatedIconAnimation(...listenPoses));
const BradPromptIcon = posed.div(rotatedIconAnimation(...questionPoses));
const BradWrongIcon = posed.div(rotatedIconAnimation(...wrongPoses));
const BradRightIcon = posed.div(rotatedIconAnimation(...rightPoses));

const TryHint = posed.div({
    ...keysToSingleValue(_.without(allPoses, 'wait-try'), {
        scale: 0,
    }),
    'wait-try': {
        scale: 1,
        transition: {
            ease: 'easeOut',
            delay: waitTry_hint_delay,
            duration: waitTry_hint_duration,
        },
    },
});

const TryNimbus = makeNimbus({
    pose: 'wait-try',
    delay: waitTry_nimbus_delay,
    duration: waitTry_nimbus_duration,
});

const DiscussionHint = posed.div({
    ...keysToSingleValue(_.without(allPoses, 'discussion-wait'), {
        scale: 0,
    }),
    'discussion-wait': {
        scale: 1,
        transition: {
            ease: 'easeOut',
            delay: discussionWait_hint_delay,
            duration: discussionWait_hint_duration,
        },
    },
});

const DiscussionArrow = posed.div({
    ...keysToSingleValue(_.without(allPoses, 'discussion-wait'), {
        scale: 0,
    }),
    'discussion-wait': {
        scale: 1,
        transition: {
            ease: 'easeOut',
            delay: discussionWait_hint_arrow_delay,
            duration: discussionWait_hint_arrow_duration,
        },
    },
});

const DiscussionNimbus = makeNimbus({
    pose: 'discussion-wait',
    delay: discussionWait_hint_nimbus_delay,
    duration: discussionWait_hint_nimbus_duration,
});

export default function Brad(props) {
    const {playingAudio, stage, teamDirection, onClick, ...restProps} = props;

    useEffect(() => {
        const needPlay = playingAudio || bradTransitionPoses.includes(props.stage);
        if (needPlay) {
            audioBundle.play('brad');
        }
        return () => audioBundle.pause('brad');
    }, [props.stage, playingAudio]);

    return (
        <Container style={{position: 'relative'}} {...restProps}>
            <div className={styles.clickableArea} onClick={onClick}>
                <BradWaitIcon className={styles.bradWaitIcon} />
                <BradListenIcon className={styles.bradListenIcon} />
                <BradPromptIcon className={styles.bradPromptIcon} />
                <BradWrongIcon className={styles.bradWrongIcon} />
                <BradRightIcon className={styles.bradRightIcon} />
            </div>

            {renderTryHint(teamDirection)}
            {renderDiscussionHint()}
        </Container>
    );
}

function renderTryHint(teamDirection) {
    const containerStyle = {
        left: teamDirection > 0 ? '80%' : 'auto',
        right: teamDirection < 0 ? '80%' : 'auto',
        transformOrigin: teamDirection > 0 ? '0 0' : '100% 0',
    };

    const arrowStyle = {
        transform: `scaleX(${teamDirection})`,
    };

    const nimbusStyle = {
        left: teamDirection > 0 ? '29%' : 'auto',
        right: teamDirection < 0 ? '29%' : 'auto',
    };

    return (
        <TryHint style={containerStyle} className={styles.tryHintContainer}>
            <div style={arrowStyle} className={styles.arrow} />
            <div className={styles.hint}>Start music by clicking Brad!</div>
            <TryNimbus
                className={styles.nimbus}
                style={nimbusStyle}
                count={9}
                angle1={-90 + teamDirection * 10}
                angle2={90 - teamDirection * 10}
                direction={teamDirection}
                radius1={0.8}
                radius2={1}
            />
        </TryHint>
    );
}

function renderDiscussionHint() {
    return (
        <div className={styles.discussionHintContainer}>
            <DiscussionArrow className={styles.arrow} />
            <DiscussionHint className={styles.hint}>
                <div>Click Brad to hear the excerpt.</div>
                <div className={styles.spacer} />
                <div>Pay attention to the lyrics.</div>
                <div>Then, analyze and discuss!</div>
            </DiscussionHint>
            <DiscussionNimbus
                className={styles.nimbus}
                count={9}
                angle1={-55}
                angle2={115}
                direction={1}
                radius1={0.8}
                radius2={1}
            />
        </div>
    );
}
